import React from 'react';
import axios from 'axios';
import {
  Divider,
  Container,
  Typography,
  Card,
  CardContent,
  Box,
  Button,
  Alert,
  CircularProgress,
  Grid,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  useMediaQuery,
  useTheme,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import { Public, NavigateBefore, NavigateNext } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import CitySearch from '../components/forms/CitySearch';
import BirthChartDisplay from '../components/charts/BirthChartDisplay';
import { isOuterPlanetKey, useUserPreferences } from '../contexts/UserPreferencesContext';

const API_BASE_URL = process.env.REACT_APP_STUDIO_API_URL || 'http://localhost:4173/api/studio/v1';

interface City {
  id: string;
  name: string;
  displayName: string;
  coordinates: {
    latitude: number;
    longitude: number;
  };
  timezone: {
    name: string;
    offset: number;
  };
  population: number;
  admin1?: {
    name: string;
    code?: string;
  };
  country?: {
    name: string;
    code: string;
  };
}

interface TransitResponse {
  planets: Record<string, any>;
  houses: any;
  calculationData: {
    calculatedAt: string;
  };
}

interface CurrentTransitProps {
  embedded?: boolean;
}

const formatDateForInput = (date: Date): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const formatTimeForInput = (date: Date): string => {
  const hh = String(date.getHours()).padStart(2, '0');
  const mm = String(date.getMinutes()).padStart(2, '0');
  return `${hh}:${mm}`;
};

const parseCoordinate = (value: string, min: number, max: number) => {
  const coordinate = Number(value.trim());
  return Number.isFinite(coordinate) && coordinate >= min && coordinate <= max ? coordinate : null;
};

const parseTimezoneOffset = (value: string) => {
  const normalized = value.trim();
  const match = normalized.match(/^([+-])(\d{1,2}):(\d{2})$/);
  if (match) {
    const sign = match[1] === '-' ? -1 : 1;
    const hours = Number(match[2]);
    const minutes = Number(match[3]);
    const total = sign * ((hours * 60) + minutes);
    return hours <= 14 && minutes <= 59 && total >= -720 && total <= 720 ? total : null;
  }
  const minutes = Number(normalized);
  return Number.isFinite(minutes) && minutes >= -720 && minutes <= 720 ? minutes : null;
};

const HI_PLANETS: Record<string, string> = {
  Sun: 'सूर्य', Moon: 'चंद्र', Mars: 'मंगल', Mercury: 'बुध', Jupiter: 'गुरु', Venus: 'शुक्र', Saturn: 'शनि', Rahu: 'राहु', Ketu: 'केतु',
  SUN: 'सूर्य', MOON: 'चंद्र', MARS: 'मंगल', MERCURY: 'बुध', JUPITER: 'गुरु', VENUS: 'शुक्र', SATURN: 'शनि', RAHU: 'राहु', KETU: 'केतु'
};

const HI_SIGNS: Record<string, string> = {
  Aries: 'मेष', Taurus: 'वृषभ', Gemini: 'मिथुन', Cancer: 'कर्क', Leo: 'सिंह', Virgo: 'कन्या', Libra: 'तुला', Scorpio: 'वृश्चिक', Sagittarius: 'धनु', Capricorn: 'मकर', Aquarius: 'कुंभ', Pisces: 'मीन',
  Mesha: 'मेष', Vrishabha: 'वृषभ', Mithuna: 'मिथुन', Karka: 'कर्क', Simha: 'सिंह', Kanya: 'कन्या', Tula: 'तुला', Vrischika: 'वृश्चिक', Dhanu: 'धनु', Makara: 'मकर', Kumbha: 'कुंभ', Meena: 'मीन'
};

const HI_NAKSHATRAS: Record<string, string> = {
  Ashwini: 'अश्विनी', Bharani: 'भरणी', Krittika: 'कृत्तिका', Rohini: 'रोहिणी', Mrigashira: 'मृगशीर्षा', Ardra: 'आर्द्रा', Punarvasu: 'पुनर्वसु', Pushya: 'पुष्य', Ashlesha: 'आश्लेषा', Magha: 'मघा', 'Purva Phalguni': 'पूर्वा फाल्गुनी', 'Uttara Phalguni': 'उत्तरा फाल्गुनी', Hasta: 'हस्त', Chitra: 'चित्रा', Swati: 'स्वाती', Vishakha: 'विशाखा', Anuradha: 'अनुराधा', Jyeshtha: 'ज्येष्ठा', Mula: 'मूल', 'Purva Ashadha': 'पूर्वाषाढ़ा', 'Uttara Ashadha': 'उत्तराषाढ़ा', Shravana: 'श्रवण', Dhanishta: 'धनिष्ठा', Shatabhisha: 'शतभिषा', 'Purva Bhadrapada': 'पूर्वा भाद्रपदा', 'Uttara Bhadrapada': 'उत्तरा भाद्रपदा', Revati: 'रेवती'
};

const localizeManualTransitValue = (value: string | undefined, isHindi: boolean, kind: 'planet' | 'sign' | 'nakshatra') => {
  if (!isHindi || !value) return value || '—';
  if (kind === 'planet') return HI_PLANETS[value] || value;
  if (kind === 'sign') return HI_SIGNS[value] || value;
  return HI_NAKSHATRAS[value] || value;
};

const CurrentTransit: React.FC<CurrentTransitProps> = ({ embedded = false }) => {
  const { t, i18n } = useTranslation();
  const { showOuterPlanets } = useUserPreferences();
  const shouldShowPlanet = (key: string, planet: any) => showOuterPlanets || !isOuterPlanetKey(planet?.name || key);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const isMediumScreen = useMediaQuery(theme.breakpoints.down('md'));
  const chartDisplaySize = isSmallScreen ? 320 : isMediumScreen ? 420 : 500;
  const now = React.useMemo(() => new Date(), []);
  const [selectedCity, setSelectedCity] = React.useState<City | null>(null);
  const [useCustomCoordinates, setUseCustomCoordinates] = React.useState(false);
  const [customLatitude, setCustomLatitude] = React.useState('');
  const [customLongitude, setCustomLongitude] = React.useState('');
  const [customTimezone, setCustomTimezone] = React.useState('+05:30');
  const [selectedDate, setSelectedDate] = React.useState<string>(formatDateForInput(now));
  const [selectedTime, setSelectedTime] = React.useState<string>(formatTimeForInput(now));
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [chartData, setChartData] = React.useState<TransitResponse | null>(null);

  React.useEffect(() => {
    document.title = `${t('transit.manual.title')} - Kundlistar`;
  }, [t]);

  const calculateTransitForDate = async (dateOverride?: string) => {
    const latitude = useCustomCoordinates ? parseCoordinate(customLatitude, -90, 90) : selectedCity?.coordinates.latitude;
    const longitude = useCustomCoordinates ? parseCoordinate(customLongitude, -180, 180) : selectedCity?.coordinates.longitude;
    const timezoneOffset = useCustomCoordinates ? parseTimezoneOffset(customTimezone) : selectedCity?.timezone.offset;
    const cityName = useCustomCoordinates ? 'Custom Location' : selectedCity?.name;

    if (useCustomCoordinates) {
      if (latitude === null || longitude === null || timezoneOffset === null) {
        setError('Please enter valid latitude, longitude, and timezone offset like +05:30 or 330.');
        return;
      }
    } else if (!selectedCity) {
      setError(t('transit.manual.requiredCity'));
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setChartData(null);

      const dateToUse = dateOverride || selectedDate;

      const response = await axios.post(`${API_BASE_URL}/charts/transit`, {
        city: cityName,
        latitude,
        longitude,
        timezoneOffset,
        date: dateToUse,
        time: selectedTime
      });

      setChartData(response.data.data.chart);
    } catch (err: any) {
      setError(err.response?.data?.message || t('transit.manual.calculateError'));
    } finally {
      setLoading(false);
    }
  };

  const isHindi = Boolean(i18n.resolvedLanguage?.startsWith('hi') || i18n.language?.startsWith('hi'));
  const locale = isHindi ? 'hi-IN' : 'en-IN';
  const selectedLocationName = (useCustomCoordinates ? 'Custom Location' : selectedCity?.displayName) || 'Transit Location';
  const selectedLatitude = useCustomCoordinates ? parseCoordinate(customLatitude, -90, 90) : selectedCity?.coordinates.latitude;
  const selectedLongitude = useCustomCoordinates ? parseCoordinate(customLongitude, -180, 180) : selectedCity?.coordinates.longitude;

  const handleCalculate = async () => {
    await calculateTransitForDate();
  };

  const shiftTransitDay = async (direction: -1 | 1) => {
    const baseDate = new Date(`${selectedDate}T00:00:00`);
    baseDate.setDate(baseDate.getDate() + direction);
    const nextDate = formatDateForInput(baseDate);
    setSelectedDate(nextDate);
    await calculateTransitForDate(nextDate);
  };

  const content = (
    <>
      {!embedded && (
        <>
          <Typography variant="h4" sx={{ mb: 1, textAlign: 'center', fontWeight: 600 }}>
            {t('transit.manual.title')}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4, textAlign: 'center' }}>
            {t('transit.manual.subtitle')}
          </Typography>
        </>
      )}

      <Card sx={{ borderRadius: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="stretch">
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControlLabel
                sx={{ mb: 1 }}
                control={
                  <Checkbox
                    checked={useCustomCoordinates}
                    onChange={(event) => {
                      setUseCustomCoordinates(event.target.checked);
                      setError(null);
                    }}
                  />
                }
                label="Use custom latitude / longitude"
              />
              {useCustomCoordinates ? (
                <Grid container spacing={1}>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <TextField
                      fullWidth
                      label="Latitude"
                      value={customLatitude}
                      onChange={(e) => setCustomLatitude(e.target.value.replace(/[^0-9.-]/g, ''))}
                      placeholder="28.6139"
                      inputProps={{ inputMode: 'decimal' }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <TextField
                      fullWidth
                      label="Longitude"
                      value={customLongitude}
                      onChange={(e) => setCustomLongitude(e.target.value.replace(/[^0-9.-]/g, ''))}
                      placeholder="77.2090"
                      inputProps={{ inputMode: 'decimal' }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <TextField
                      fullWidth
                      label="Timezone"
                      value={customTimezone}
                      onChange={(e) => setCustomTimezone(e.target.value.replace(/[^0-9:+-]/g, ''))}
                      placeholder="+05:30"
                      helperText="UTC offset"
                    />
                  </Grid>
                </Grid>
              ) : (
                <CitySearch
                  value={selectedCity}
                  onChange={(city) => {
                    setSelectedCity(city);
                    setError(null);
                  }}
                  label={t('transit.manual.city')}
                  placeholder={t('transit.manual.cityPlaceholder')}
                  required
                />
              )}
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
              <TextField
                fullWidth
                type="date"
                label={t('transit.manual.date')}
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
              <TextField
                fullWidth
                type="time"
                label={t('transit.manual.time')}
                value={selectedTime}
                onChange={(e) => setSelectedTime(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 2 }}>
              <Button
                fullWidth
                variant="contained"
                startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <Public />}
                onClick={handleCalculate}
                disabled={loading || (!selectedCity && !useCustomCoordinates)}
                sx={{ height: '100%', minHeight: 56, fontWeight: 600 }}
              >
                {loading ? t('transit.manual.calculating') : t('transit.manual.showTransit')}
              </Button>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, pt: 0.5 }}>
                <Button
                  variant="outlined"
                  startIcon={<NavigateBefore />}
                  onClick={() => shiftTransitDay(-1)}
                  disabled={loading || (!selectedCity && !useCustomCoordinates)}
                >
                  {t('transit.manual.previousDay')}
                </Button>
                <Button
                  variant="outlined"
                  endIcon={<NavigateNext />}
                  onClick={() => shiftTransitDay(1)}
                  disabled={loading || (!selectedCity && !useCustomCoordinates)}
                >
                  {t('transit.manual.nextDay')}
                </Button>
              </Box>
            </Grid>
          </Grid>

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </CardContent>
      </Card>

      {chartData && (selectedCity || useCustomCoordinates) && (
        <Box sx={{ mt: 4 }}>
          <Card sx={{ mb: 3, borderRadius: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 0.5 }}>{selectedLocationName}</Typography>
              <Typography variant="body2" color="text.secondary">
                {t('transit.manual.transitTime')}: {new Date(chartData.calculationData.calculatedAt).toLocaleString(locale)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('transit.manual.coordinates')}: {Number(selectedLatitude).toFixed(4)}, {Number(selectedLongitude).toFixed(4)}
              </Typography>
            </CardContent>
          </Card>

          <Card sx={{ borderRadius: 3, mb: 3 }}>
            <CardContent sx={{ display: 'flex', justifyContent: 'center' }}>
              <BirthChartDisplay
                chartSize={chartDisplaySize}
                chartData={{
                  ...chartData,
                  birthDetails: {
                    name: isHindi ? `${selectedLocationName} गोचर` : `${selectedLocationName} Transit`,
                    dateOfBirth: chartData.calculationData.calculatedAt,
                    timeOfBirth: new Date(chartData.calculationData.calculatedAt).toISOString(),
                    placeOfBirth: {
                      name: selectedLocationName,
                      latitude: Number(selectedLatitude),
                      longitude: Number(selectedLongitude)
                    },
                    gender: 'Other'
                  },
                  bhavaChalit: {
                    cusps: chartData.houses?.cusps || {},
                    system: isHindi ? 'गोचर भाव चलित' : 'Transit Bhava Chalit'
                  }
                }}
              />
            </CardContent>
          </Card>

          <Card sx={{ borderRadius: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>{t('transit.manual.planetaryPositions')}</Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('transit.manual.planet')}</TableCell>
                      <TableCell>{t('transit.manual.sign')}</TableCell>
                      <TableCell>{t('transit.manual.degree')}</TableCell>
                      <TableCell>{t('transit.manual.house')}</TableCell>
                      <TableCell>{t('transit.manual.nakshatra')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Object.entries(chartData.planets).filter(([key, planet]: [string, any]) => shouldShowPlanet(key, planet)).map(([key, planet]: [string, any]) => (
                      <TableRow key={key}>
                        <TableCell>{localizeManualTransitValue(planet.name || key, isHindi, 'planet')}</TableCell>
                        <TableCell>{localizeManualTransitValue(planet.sign?.name, isHindi, 'sign')}</TableCell>
                        <TableCell>{planet.degree?.formatted}</TableCell>
                        <TableCell>{planet.house}</TableCell>
                        <TableCell>{localizeManualTransitValue(planet.nakshatra?.name, isHindi, 'nakshatra')}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Box>
      )}
    </>
  );

  if (embedded) {
    return (
      <Box sx={{ pt: 1 }}>
        <Box sx={{ mb: 2.5 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {t('transit.tabs.manual')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {t('transit.manual.subtitle')}
          </Typography>
        </Box>
        <Divider sx={{ mb: 3 }} />
        {content}
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {content}
    </Container>
  );
};

export default CurrentTransit;
